import { useQuery } from '@apollo/client';
import type { LaunchPadData, PublicCollectionData } from '@mogul/api-client';
import { CollectionInner } from '@mogul/shared';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { useHistory, useParams } from 'react-router-dom';

import { collectionsApi, launchPadApi } from '../../../api';
import { MarketplaceListComponent } from '../../../components/marketplace/MarketplaceList.component';
import { SearchBar } from '../../../components/search-bar/SearchBar.component';
import type { searchOptions } from '../../../components/search-bar/searchOptions/SearchOptions.component';
import { SearchOptions } from '../../../components/search-bar/searchOptions/SearchOptions.component';
import type { Collection } from '../../../lib/graph/MogulMarketplace';
import assetCollectionListQuery from '../../../lib/graph/queries/assetCollectionListQuery';
import assetListPriceQuery from '../../../lib/graph/queries/assetListPriceQuery';
import collectionQuery from '../../../lib/graph/queries/collectionQuery';
import { initHideOnScrollEffect } from '../../../utils/searchbar.util';
import styles from './CollectionPage.module.css';

const INITIAL_SEARCH_OPTIONS = {
  network: 'all',
  sortBy: 'mintedTime',
  sortDirection: 'desc',
  range: {
    netOption: 'bsc',
    minVal: '',
    maxVal: '',
  },
  listingType: '',
};

interface Params {
  nftContractAddress: string;
  chain: string;
}

export const CollectionPage: FC = () => {
  const { nftContractAddress: nftContractAddressCheckSum } =
    useParams<Params>();
  const [search, setSearch] = useState<string>('');
  const [searchOption, setSearchOptions] = useState<searchOptions | undefined>(
    INITIAL_SEARCH_OPTIONS,
  );

  const [collection, setCollection] =
    useState<PublicCollectionData | null | undefined>();

  const [launchPad, setLaunchPad] =
    useState<LaunchPadData | null | undefined>();

  const nftContractAddress = collection?.contractAddress.toLowerCase() ?? '';
  const history = useHistory();

  const [supply, setSupply] = useState<number>(0);
  const [owners, setOwners] = useState<number>(0);

  const { data: collectionData } = useQuery<{ collection?: Collection }>(
    collectionQuery,
    {
      variables: { nftContractAddress },
      pollInterval: 5000,
      context: { network: collection?.network },
    },
  );

  useEffect(() => {
    const getCollectionInfo = async () => {
      try {
        const collectionInfo =
          await collectionsApi.getCollectionByAddressOrSlug({
            addressOrSlug: nftContractAddressCheckSum,
          });

        setCollection(collectionInfo.data.data);
      } catch (e) {
        history.push('/platform/dashboard');
      }
    };
    void getCollectionInfo();
  }, []);

  useEffect(() => {
    if (!collectionData?.collection) {
      return;
    }

    setSupply(collectionData.collection.totalSupply);
    setOwners(collectionData.collection.totalOwners);
  }, [collectionData?.collection]);

  useEffect(() => {
    // Set head > meta tags regarding OpenGraph manually.
    // React Helmet appends to end of header, which might not get read by bots
    const openGraphData: any = {
      'og:title': collection?.name,
      'og:description': collection?.description,
      'og:image': collection?.logoImageUrl,
      'og:url': window.location,
    };

    for (const property in openGraphData) {
      const headMetaElement = document.querySelector(
        `head>meta[property*="${property}"]`,
      );
      headMetaElement?.setAttribute('content', openGraphData[property]);
    }
  }, [collection?.logoImageUrl, collection?.name, collection?.description]);

  useEffect(() => initHideOnScrollEffect('searchBarWrap', 0, 0), []);

  useEffect(() => {
    const getLaunchPadInfo = async () => {
      if (collection) {
        try {
          const result = await launchPadApi.getLaunchPadFromCollection({
            id: collection.id,
          });
          setLaunchPad(result.data.data);
        } catch (err) {
          console.log(
            `Getting launchpad from collection ${collection.id} failed with 404`,
          );
        }
      }
    };
    void getLaunchPadInfo();
  }, [collection]);

  const goToLandingPage = () => {
    history.push(`/platform/showcase/${collection?.slug}`);
  };

  return (
    <div className="flex flex-col items-center h-auto bg-container bg-cover bg-repeat w-auto md:p-0 pb-3 md:pb-0">
      <Toaster />
      <div
        className={
          styles.searchBarWrap +
          ' sticky top-0 w-full flex justify-center items-center md:py-3 sm:pt-4 sm:pb-3 z-20'
        }
        id={'searchBarWrap'}
      >
        <SearchBar
          tailwindOverride={'lg:w-2/3 sm:w-10/12 w-full'}
          viewableHeightDesktop={0}
          viewableHeightMobile={0}
          setSearch={setSearch}
        />
      </div>

      <div className="mb-20 w-full">
        <CollectionInner
          collection={collection}
          launchPad={launchPad}
          supply={supply}
          owners={owners}
          nftContractAddress={nftContractAddress}
          goToLandingPage={goToLandingPage}
        />
        <div className="flex flex-col items-center">
          <div className="mr-4 sm:mx-0">
            <SearchOptions setSearchOptions={setSearchOptions} />
          </div>
          <div className={styles.collectionContainer + ' lg:w-11/12 px-4'}>
            {collection && (
              <MarketplaceListComponent
                search={search}
                options={searchOption}
                gql={assetCollectionListQuery}
                gql2={assetListPriceQuery}
                nftContractAddress={collection.contractAddress}
                network={collection.network}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
